import { useCallback, useEffect, useState } from 'react';
import schedulerService from '../services/schedulerService';
import { buildSchedulerPayload } from '../adapters/schedulerAdapter';
import { normalizeSchedulerList } from '../utils/schedulerListUtils';

export default function useSchedulerAutomations({ formId, workspaceId, enabled = true }) {
  const [schedulers, setSchedulers] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [loadError, setLoadError] = useState('');
  const [isSaving, setIsSaving] = useState(false);

  const loadSchedulers = useCallback(async () => {
    if (!enabled || !formId) return;
    setIsLoading(true);
    setLoadError('');
    try {
      const response = await schedulerService.listSchedulers(formId);
      setSchedulers(normalizeSchedulerList(response?.data));
    } catch (error) {
      setLoadError(error?.message || 'Failed to load automations.');
    } finally {
      setIsLoading(false);
    }
  }, [enabled, formId]);

  useEffect(() => {
    loadSchedulers();
  }, [loadSchedulers]);

  const saveScheduler = useCallback(
    async (form) => {
      setIsSaving(true);
      try {
        const payload = buildSchedulerPayload({
          ...form,
          campaign_id: form.campaign_id ?? workspaceId,
          lead_form_id: form.lead_form_id ?? formId,
        });
        const response = form.scheduler_id
          ? await schedulerService.updateScheduler(payload)
          : await schedulerService.createScheduler(payload);
        if (response?.status === 200) {
          await loadSchedulers();
          return { success: true };
        }
        return { success: false };
      } catch (error) {
        return { success: false, error: error?.message };
      } finally {
        setIsSaving(false);
      }
    },
    [formId, loadSchedulers, workspaceId]
  );

  const deleteScheduler = useCallback(
    async (schedulerId) => {
      const response = await schedulerService.deleteScheduler(schedulerId);
      if (response?.status === 200) {
        setSchedulers((prev) =>
          prev.filter((item) => String(item.scheduler_id) !== String(schedulerId))
        );
        return { success: true };
      }
      return { success: false };
    },
    []
  );

  return {
    schedulers,
    isLoading,
    loadError,
    isSaving,
    reload: loadSchedulers,
    saveScheduler,
    deleteScheduler,
  };
}
